import {
  ContactNumberLabel,
  Gender,
  IContactNumber,
  IHealthInsuranceDetails,
  IPractice,
  PatientStatus,
  type IBrand,
  type IMedicareCard,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  BasePatientDestinationEntity,
  PATIENT_RESOURCE_TYPE,
} from '../../../destination/entities/patient';
import {
  getError,
  INamedDocument,
  snapshotCombineLatest,
  Timestamp,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  WithPrimaryKey,
  type WithRef,
} from '@principle-theorem/shared';
import {
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  IPracticeMigration,
  ITranslationMap,
  SourceEntityRecordStatus,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  IPracticeWorksPatient,
  PatientSourceEntity,
  PracticeWorksPatient,
} from '../../source/entities/patients';
import { ExistingPatientDestinationEntity } from '../../../exact/destination/entities/existing-patients';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { PracticeWorksStafferMappingHandler } from '../mappings/staff';
import { combineLatest, Observable, of } from 'rxjs';
import { TranslationMapHandler } from '../../../translation-map';
import { PracticeMigration } from '../../../practice-migrations';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { map, switchMap } from 'rxjs/operators';
import { resolveExactStaffer } from '../../../exact/destination/mappings/staff';
import { Patient, stafferToNamedDoc } from '@principle-theorem/principle-core';
import { compact } from 'lodash';
import {
  resolveExactStaffLocation,
  StaffToPracticeMapping,
} from '../../../exact/destination/mappings/practitioner-to-practice-mapping';
import { StafferDestinationEntity } from './staff';

export const PATIENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_RESOURCE_TYPE,
    label: 'Patients',
    description: ``,
  },
});

export interface IPatientMigrationData {
  sourcePatientId: string;
  patient: IPatient;
  createdAt?: Timestamp;
}

export interface IPatientJobData {
  sourcePatient: IGetRecordResponse<PracticeWorksPatient>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  brand: WithRef<IBrand>;
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
}
export class PatientDestinationEntity extends BasePatientDestinationEntity<PracticeWorksPatient> {
  destinationEntity = PATIENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  override destinationEntities = {
    existingPatients: new ExistingPatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  override filters = [
    new PatientIdFilter<IPatientJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  customMappings = {
    staff: new PracticeWorksStafferMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientJobData[]> {
    const brand$ = PracticeMigration.brand$(migration);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const staffToPractice$ =
      this.customMappings.staffToPractice.getRecords(translationMap);

    return snapshotCombineLatest([brand$, staff$, staffToPractice$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [brand, staff, staffToPractice]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          brand,
          staff,
          staffToPractice,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientJobData
  ): Promise<
    | IPatientMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    if (data.sourcePatient.record.status === SourceEntityRecordStatus.Invalid) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        'Source patient is invalid'
      );
    }

    try {
      const patient = data.sourcePatient.data;
      const sourcePatientId = this.sourceEntities.patients
        .getSourceRecordId(patient)
        .toString();

      const preferredDentist = await getPractitioner(
        patient.preferredProviderId,
        translationMap,
        data.staff
      );
      const preferredPractice = await getStaffPractice(
        patient.preferredProviderId.toString(),
        translationMap,
        data.staffToPractice,
        migration.configuration.practices
      );
      const createdAt = patient.createdDate
        ? toTimestamp(
            toMomentTz(patient.createdDate, migration.configuration.timezone)
          )
        : undefined;

      return {
        sourcePatientId,
        patient: Patient.init({
          name: getName(patient),
          email: patient.email,
          address: getAddress(patient),
          gender: getGender(patient),
          contactNumbers: getContactNumnbers(patient),
          dateOfBirth: patient.birthDate,
          referenceId: sourcePatientId,
          preferredDentist,
          preferredPractice,
          medicareCard: getMedicareCard(patient),
          healthInsuranceDetails: getHealthInsurance(patient),
          status: patient.noLongerPatient
            ? PatientStatus.Inactive
            : PatientStatus.Active,
        }),
        createdAt,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }
}

function getName(patient: IPracticeWorksPatient): string {
  return `${patient.firstName} ${patient.lastName}`;
}

function getGender(patient: IPracticeWorksPatient): Gender {
  return patient.sex === 0 ? Gender.Male : Gender.Female;
}

function getHealthInsurance(
  patient: IPracticeWorksPatient
): IHealthInsuranceDetails | undefined {
  if (!patient.insuranceCard) {
    return;
  }
  return {
    provider: patient.insuranceProvider,
    membershipNumber: patient.insuranceCard,
  };
}

function getAddress(patient: IPracticeWorksPatient): string | undefined {
  const addressLines = compact([
    patient.address?.trim(),
    patient.address2?.trim(),
    patient.city?.trim(),
    patient.state?.trim(),
    patient.postcode?.trim(),
  ]);
  if (!addressLines.length) {
    return;
  }
  return addressLines.join(', ');
}

function getContactNumnbers(patient: IPracticeWorksPatient): IContactNumber[] {
  return compact([
    patient.cellPhone
      ? { label: ContactNumberLabel.Mobile, number: patient.cellPhone }
      : undefined,
    patient.homePhone
      ? { label: ContactNumberLabel.Home, number: patient.homePhone }
      : undefined,
    patient.workPhone
      ? { label: ContactNumberLabel.Work, number: patient.workPhone }
      : undefined,
    patient.workPhone2
      ? { label: ContactNumberLabel.Work, number: patient.workPhone2 }
      : undefined,
  ]);
}

async function getPractitioner(
  providerId: number,
  translationMap: TranslationMapHandler,
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[]
): Promise<INamedDocument<IStaffer> | undefined> {
  const staffer = await resolveExactStaffer(
    providerId.toString(),
    translationMap,
    staff
  );
  return staffer ? stafferToNamedDoc(staffer) : undefined;
}

function getMedicareCard(
  patient: IPracticeWorksPatient
): IMedicareCard | undefined {
  if (!patient.medicareNumber) {
    return;
  }

  const match = patient.medicareNumber.match(/^(\d+)[\s\-/#]+(\d+)$/);
  if (!match) {
    return;
  }
  const [, number, subNumerate] = match;

  return {
    number,
    subNumerate,
  };
}

async function getStaffPractice(
  initials: string,
  translationMap: TranslationMapHandler,
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[],
  migrationPractices: INamedDocument<IPractice>[]
): Promise<INamedDocument<IPractice> | undefined> {
  const practice = await resolveExactStaffLocation(
    initials,
    translationMap,
    staffToPractice,
    migrationPractices
  );
  return practice ? toNamedDocument(practice) : undefined;
}
